import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { getEventReportApi, getEventSummaryApi, getEventTypesApi } from '../../../apis/apis'
import { EventReportEntry, EventSummary, GetEventReportArgs, Paginated } from '../../../apis/types'
import { AppDispatch, RootState } from '../../../shared/redux/store'

const SLICE_NAME = 'EVENT_HANDLING_REPORT_SLICE'

export const getEventSummary = createAsyncThunk<
  EventSummary,
  GetEventReportArgs,
  {
    state: RootState
    dispatch: AppDispatch
    rejectValue: { err: boolean }
  }
>(`${SLICE_NAME}/getEventSummary`, async (query, { rejectWithValue }) => {
  try {
    const response = await getEventSummaryApi(query)
    return response
  } catch (err) {
    return rejectWithValue({
      err: err !== undefined,
    })
  }
})

export const getEventReport = createAsyncThunk<
  Paginated<EventReportEntry>,
  GetEventReportArgs,
  {
    state: RootState
    dispatch: AppDispatch
    rejectValue: { err: boolean }
  }
>(`${SLICE_NAME}/getEventReport`, async (query, { rejectWithValue }) => {
  try {
    const response = await getEventReportApi(query)
    return response
  } catch (err) {
    return rejectWithValue({
      err: err !== undefined,
    })
  }
})

export const getEventTypes = createAsyncThunk<
  string[],
  GetEventReportArgs,
  {
    state: RootState
    dispatch: AppDispatch
    rejectValue: { err: boolean }
  }
>(`${SLICE_NAME}/getEventTypes`, async (query, { rejectWithValue }) => {
  try {
    const response = await getEventTypesApi(query)
    return response
  } catch (err) {
    return rejectWithValue({
      err: err !== undefined,
    })
  }
})

interface state {
  summaryLoading: boolean
  eventsLoading: boolean
  eventTypesLoading: boolean
  error: boolean
  summary?: EventSummary
  events?: Paginated<EventReportEntry>
  eventTypes: string[]
}

const initialState: state = {
  summaryLoading: false,
  eventsLoading: false,
  eventTypesLoading: false,
  error: false,
  summary: undefined,
  events: undefined,
  eventTypes: [],
}

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getEventSummary.pending, (state) => {
      state.summaryLoading = true
      state.error = false
    })
    builder.addCase(getEventSummary.fulfilled, (state, { payload }) => {
      state.summaryLoading = false
      state.error = false
      state.summary = payload
    })
    builder.addCase(getEventSummary.rejected, (state) => {
      state.summaryLoading = false
      state.error = true
    })
    builder.addCase(getEventReport.pending, (state) => {
      state.eventsLoading = true
      state.error = false
    })
    builder.addCase(getEventReport.fulfilled, (state, { payload }) => {
      state.eventsLoading = false
      state.error = false
      state.events = payload
    })
    builder.addCase(getEventReport.rejected, (state) => {
      state.eventsLoading = false
      state.error = true
    })
    builder.addCase(getEventTypes.pending, (state) => {
      state.eventTypesLoading = true
      state.error = false
    })
    builder.addCase(getEventTypes.fulfilled, (state, { payload }) => {
      state.eventTypesLoading = false
      state.error = false
      state.eventTypes = payload
    })
    builder.addCase(getEventTypes.rejected, (state) => {
      state.eventTypesLoading = false
      state.error = true
      state.eventTypes = []
    })
  },
})

export default slice.reducer
